/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import * as Toast from "@radix-ui/react-toast";
import { useEffect, useRef, useState } from "react";

import type { ThemeType } from "../../../../global/emotion";
import {
  dropShadow,
  numericMilliseconds,
  numericPercentages,
  numericValues,
  radius,
  shades,
  spacing,
  stroke,
} from "../../../../global/emotion";
import type { HallowElementProps } from "../../../types/hallowElementProps.types";
import type { IconElement } from "../../_base/icons/Icon/Icon";

const defaultStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  background: theme.semanticColors.secondary,
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.full,
  borderWidth: stroke.light,
  boxShadow: `${numericValues[0]} ${dropShadow.s} ${dropShadow.ms} ${numericValues[0]} ${shades.shade10}`,
  color: theme.semanticColors.primary,
  columnGap: spacing.ms,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  justifyContent: "center",
  opacity: 0,
  padding: spacing.s,
  // we can't use variables here because then the CSS prop is invalid
  transform: "scale(0.7) translateY(50px)",
  transitionDelay: numericValues[0],
  transitionDuration: `${numericMilliseconds[400]}, ${numericMilliseconds[500]}`,
  transitionProperty: "opacity, transform",
  transitionTimingFunction: "cubic-bezier(.5,.87,.3,1.11)",
  width: numericPercentages[100],
});
const openStyle: Interpolation = {
  opacity: 1,
  transform: "scale(1) translateY(0)",
  transitionDelay: "100ms, 0",
  transitionDuration: "500ms",
};
const iconStyle: Interpolation = {
  display: "flex",
  height: spacing.xl,
  placeContent: "center",
  placeItems: "center",
  width: spacing.xl,
};

export type PopupProps = HallowElementProps<"div"> & {
  durationMs?: number;
  icon?: IconElement;
  type: Toast.ToastProps["type"];
};

export const Popup = ({
  children,
  durationMs = 5000,
  icon,
  type,
  ...props
}: PopupProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const ref = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => setOpen(true));
    setTimeout(() => requestAnimationFrame(() => setOpen(false)), durationMs);
  }, []);

  // account for in AND out animation time on duration
  return (
    <Toast.Root duration={durationMs + 1000} type={type} asChild ref={ref}>
      <div css={[defaultStyle, open && openStyle]} {...props}>
        {icon ? <div css={iconStyle}>{icon}</div> : null}
        {children}
      </div>
    </Toast.Root>
  );
};
